import { BaseCollection } from './base-collection.model';

export interface LoginCredentials {
  username: string;
  password: string;
  mfaCode: string;
  rememberDevice: boolean;
}

export interface MfaCredentials {
  username: string;
  recoveryCode: string;
}

export interface MfaSetup {
  recoveryCodes: string[];
  qrCode: string;
  secret: string;
}

export interface BasicUser {
  userId: number;
  id: number;
  fullName: string;
  username: string;
  email: string;
}

export interface BasicUserCollection extends BaseCollection {
  content: BasicUser[];
}

export type { UserAdmin, UserAdminWithTestCount, UserAdminCollection } from './user-admin.model';

export class Role {
  static ADMIN = 'admin';
  static MAIN = 'mainaccount';
  static DEPUTY = 'deputy';
  static SUB = 'subaccount';
  static GUEST = 'guest';
}

export class PaymentType {
  static PREPAID = 'PREPAID';
  static POSTPAID = 'POSTPAID';
}

export enum AgreementStatusFilter {
  HAS_AGREEMENT = 'HAS_AGREEMENT',
  NO_AGREEMENT = 'NO_AGREEMENT',
}

export enum LastTopUpRange {
  UNDER_1_MONTH = 'UNDER_1_MONTH',
  MONTHS_1_TO_3 = 'MONTHS_1_TO_3',
  MONTHS_3_TO_6 = 'MONTHS_3_TO_6',
  MONTHS_6_TO_12 = 'MONTHS_6_TO_12',
  MONTHS_12_TO_24 = 'MONTHS_12_TO_24',
  OVER_24_MONTHS = 'OVER_24_MONTHS',
  NEVER = 'NEVER',
}

export type Roles = 'admin' | 'mainaccount' | 'deputy' | 'manager' | 'subaccount';

export interface AuthUser {
  id: number;
  role: Roles;
  parentId: number;
  paymentType: string;
  impersonated: boolean;
  enabled: boolean;
  email: string;
  emailVerified: boolean;
  username: string;
  fullName: string;
  ownerEmail?: string;
  showAllResults?: boolean;
  defaultLntTemplate?: number;
  testTtlMin?: number;
  testTtl?: number;
  companyName?: string;
  phone?: string;
  websiteUrl?: string;
  vatNumber?: string;
  vatPercentage?: number;
  emailForAlert?: string;
  emailForInvoice?: string;
  dataProtectionOfficerEmail?: string;
  legalRepresentativeEmail?: string;
  procurementEmail?: string;
  countryName?: string;
  countryCode?: string;
  departmentName?: string;
  addressCity: string;
  addressZip?: string;
  addressStreet: string;
  addressState?: string;
  minBalance?: number;
  testRetriesEnabled?: boolean;
  mfaEnabled?: boolean;
  ipWhitelistEnabled?: boolean;
  ipWhitelistAddressList?: string;
  moAllowedNumbersEnabled?: boolean;
  companyDetailsVerified: boolean;
  isOnboarded: boolean;
  clicktermAgreementFlowEnabled: boolean | null;
  customAgreement: boolean | null;
  accountRegisteredBeforeClickwrap: boolean | null;
}

export interface AccountUser {
  emailForAlert: string;
  role: string;
  addressZip: string;
  companyName: string;
  departmentName?: string;
  fullName: string;
  testTtl: number;
  paymentType: string;
  password?: string;
  emailForInvoice: string;
  dataProtectionOfficerEmail?: string;
  legalRepresentativeEmail?: string;
  procurementEmail?: string;
  minBalance: number;
  phone: string;
  websiteUrl: string;
  addressStreet: string;
  addressState: string;
  id: number;
  countryName: string;
  countryCode?: string;
  email: string;
  testRetriesEnabled: boolean;
  username: string;
  vatNumber: string;
  vatPercentage?: number;
  addressCity: string;
  mfaEnabled?: boolean;
  ipWhitelistEnabled: boolean;
  ipWhitelistAddressList: string;
}

export interface UserCompanyDetails {
  addressZip: string;
  companyName: string;
  websiteUrl: string;
  addressStreet: string;
  addressState: string;
  countryName: string;
  countryCode?: string;
  vatNumber: string;
  addressCity: string;
}

export interface UpdatePasswordModel {
  token?: string;
  currentPassword?: string;
  newPassword?: string;
  repeatNewPassword?: string;
}

export interface UserAgreementSettingsUpdateModel {
  clicktermAgreementFlowEnabled?: boolean | null;
  customAgreement?: boolean | null;
}

export interface NewEmailModel {
  newEmail: string;
}

export type ZohoSyncStatus = 'SYNCED' | 'NOT_REQUIRED' | 'FAILED';
export type VatValidationStatus = 'UNVERIFIED' | 'VERIFIED' | 'INVALID' | 'UNAVAILABLE' | 'NOT_REQUIRED';

export interface AdminUserUpdateResult {
  saved: boolean;
  zohoSyncStatus: ZohoSyncStatus;
  warning?: string | null;
  vatValidationStatus?: VatValidationStatus | null;
}

export interface AdminVatValidationResult {
  vatNumber?: string | null;
  vatValidationStatus: VatValidationStatus;
}

export { ZohoCustomerSettings, ZohoCustomerSettingsUpdate, refreshZohoCustomerMapping } from './zoho-customer-settings.model';

export interface UserFormData {
  id: number;
  zohoContactId?: string;
  fullName: string;
  email: string;
  password: string;
  paymentType: string;
  enabled: boolean;
  emailVerified: boolean;
  sendVerificationEmail: boolean;
  username: string;
  companyName: string;
  departmentName?: string;
  websiteUrl: string;
  countryName: string;
  countryCode?: string;
  userRole: string;
  phone: string;
  emailForAlert: string;
  emailForInvoice: string;
  dataProtectionOfficerEmail?: string;
  legalRepresentativeEmail?: string;
  procurementEmail?: string;
  addressCity: string;
  addressStreet: string;
  addressState: string;
  addressZip: string;
  vatNumber: string;
  vatValidationStatus?: VatValidationStatus;
  vatPercentage: number;
  applyVat: boolean;
  vatExempt: boolean;
  testRetriesEnabled: boolean;
  rankingIndex: number;
  parentId: number;
  ownerId: number;
  testTtl: number;
  source: string;
  convertedBy: number | any;
  salesRepresentative: number | any;
  parent: BasicUser;
  owner: BasicUser;
  showAllResults: boolean;
  ipWhitelistEnabled: boolean;
  ipWhitelistAddressList: string;
  minBalance: number;
  comment: string;
}

export interface UserUpdateByAdminModel {
  userRole: string;
  username: string;
  fullName: string;
  companyName: string;
  departmentName?: string;
  parent?: BasicUser;
  owner?: BasicUser;
  phone: string;
  websiteUrl?: string;
  rankingIndex: number;
  vatNumber?: string;
  vatExempt: boolean;
  email: string;
  emailForAlert?: string;
  emailForInvoice?: string;
  dataProtectionOfficerEmail?: string;
  legalRepresentativeEmail?: string;
  procurementEmail?: string;
  paymentType: string;
  countryName: string;
  countryCode?: string;
  addressCity: string;
  addressZip?: string;
  addressStreet: string;
  addressState?: string;
  password?: string;
  emailVerified: boolean;
  sendVerificationEmail: boolean;
  testTtl: number;
  enabled: boolean;
  testRetriesEnabled: boolean;
  source: string;
  convertedBy: BasicUser;
  salesRepresentative: BasicUser;
  ipWhitelistEnabled: boolean;
  ipWhitelistAddressList: string;
  minBalance: number;
  comment: string;
}

export interface UserCreateByAdminModel {
  companyInformation: {
    id: number;
    companyName: string;
    websiteUrl: string;
    countryName: string;
    countryCode?: string;
    vatNumber: string;
    hasSmppInfrastructure: boolean;
    dataProtectionOfficerEmail?: string;
    legalRepresentativeEmail?: string;
    procurementEmail?: string;
  };
  userProperties: {
    paymentType: string;
    emailVerified: boolean;
    testRetriesEnabled: boolean;
    showAllResults: boolean;
    defaultLntTemplate: 0;
    defaultMtTemplate: 0;
    minBalance: number;
    rankingIndex: number;
    testTtl: number;
    ownerId: number;
    parentId: number;
    convertedById: number;
    salesRepresentativeId: number;
    source: string;
    comment: string;
  };
  userRole: string;
  username: string;
  password: string;
  fullName: string;
  email: string;
  phone: string;
  addressCity: string;
  addressZip: string;
  addressStreet: string;
  addressState: string;
  countryName: string;
  countryCode?: string;
  emailForAlert: string;
  emailForInvoice: string;
  enabled: boolean;
  sendVerificationEmail: boolean;
}

export interface RegistrationModel {
  username: string;
  password: string;
  fullName: string;
  email: string;
  phone: string;
  addressCity: string;
  addressZip: string;
  addressStreet: string;
  addressState: string;
  countryName: string;
  countryCode?: string;
  emailForAlert: string;
  emailForInvoice: string;
  source: string;
  recaptchaToken: string;
  clicktermUUID: string;
  clicktermSignature: string;
  companyInformation: {
    companyName: string;
    websiteUrl: string;
    countryName: string;
    countryCode?: string;
    vatNumber: string;
  };
}

export interface RegistrationMainInfoModel {
  username: string;
  password: string;
  fullName: string;
  websiteUrl: string;
  email: string;
  phone: string;
  source: string;
  recaptchaToken: string;
  clicktermUUID: string;
  clicktermSignature: string;
}

export interface RegistrationMainInfoResponseModel {
  userId: number;
  continuationToken: string;
}

export interface RegistrationCompanyBillingModel {
  continuationToken: string;
  companyName: string;
  countryName: string;
  countryCode?: string;
  addressState?: string;
  addressCity: string;
  addressStreet: string;
  addressZip?: string;
  vatNumber?: string;
  queryString?: string;
}

export type RegistrationVatStatus = 'NOT_REQUIRED' | 'NOT_PROVIDED' | 'VERIFIED' | 'VERIFIED_DOMESTIC_VAT' | 'UNVERIFIED' | 'VIES_UNAVAILABLE';

export interface RegistrationCompanyBillingResponseModel {
  vatStatus: RegistrationVatStatus;
  vatNumberStored: boolean;
  vatPercentage: number;
  applyVat: boolean;
  message?: string;
}

export interface RegistrationByInviteModel {
  username: string;
  password: string;
  fullName: string;
  phone: string;
  countryName: string;
  countryCode?: string;
  email?: string;
}

export interface SubaccountInviteResponseModel {
  email: string;
  enabled: boolean;
  parentId: number;
  paymentType: string;
  role: string;
  userId: number;
}

export interface TrustedDevice {
  id: number;
  createdAt: string;
  expireAt: string;
  lastLoginAt: string;
  lastIpAddress: string;
  lastClientAgent: string;
  internalDeviceId: string;
}

export interface TrustedDeviceCollection extends BaseCollection {
  content: TrustedDevice[];
}

export function minBalanceToMilliEuros(minBalance: number): number {
  return Math.trunc(Number(minBalance) * 1000);
}

export function minBalanceFromMilliEuros(minBalance: number): number {
  return Math.floor(Number(minBalance) / 1000);
}

export function formToCreateModel(data: UserFormData): UserCreateByAdminModel {
  const newData = Object.assign({}, data);
  if (newData.testTtl) {
    newData.testTtl = parseInt(String(newData.testTtl)) * 60;
  }

  return {
    companyInformation: {
      id: null,
      companyName: newData.companyName,
      websiteUrl: newData.websiteUrl,
      countryName: newData.countryName,
      countryCode: newData.countryCode,
      vatNumber: newData.vatNumber,
      hasSmppInfrastructure: false,
      dataProtectionOfficerEmail: newData.dataProtectionOfficerEmail,
      legalRepresentativeEmail: newData.legalRepresentativeEmail,
      procurementEmail: newData.procurementEmail,
    },
    userProperties: {
      paymentType: newData.paymentType,
      emailVerified: newData.emailVerified,
      testRetriesEnabled: newData.testRetriesEnabled,
      showAllResults: newData.showAllResults,
      defaultLntTemplate: 0,
      defaultMtTemplate: 0,
      minBalance: minBalanceToMilliEuros(newData.minBalance ?? 1),
      rankingIndex: newData.rankingIndex,
      testTtl: newData.testTtl,
      ownerId: newData.owner ? newData.owner.id : null,
      parentId: newData.parent ? newData.parent.id : null,
      convertedById: newData.convertedBy ? newData.convertedBy.id : null,
      salesRepresentativeId: newData.salesRepresentative ? newData.salesRepresentative.id : null,
      source: newData.source !== '' ? newData.source : null,
      comment: newData.comment ? newData.comment : null,
    },
    userRole: newData.userRole,
    username: newData.username,
    password: newData.password,
    fullName: newData.fullName,
    email: newData.email,
    phone: newData.phone ? newData.phone : '',
    addressCity: newData.addressCity,
    addressZip: newData.addressZip,
    addressStreet: newData.addressStreet,
    addressState: newData.addressState,
    countryName: newData.countryName,
    countryCode: newData.countryCode,
    emailForAlert: newData.emailForAlert,
    emailForInvoice: newData.emailForInvoice,
    enabled: newData.enabled,
    sendVerificationEmail: newData.sendVerificationEmail,
  };
}

export function formToUpdateModel(data: UserFormData): UserUpdateByAdminModel {
  const newData = Object.assign({}, data);

  if (newData.testTtl) {
    newData.testTtl = parseInt(String(newData.testTtl)) * 60;
  }

  return {
    userRole: newData.userRole,
    username: newData.username,
    fullName: newData.fullName,
    companyName: newData.companyName,
    departmentName: newData.departmentName ? newData.departmentName : null,
    parent: newData.parent ? newData.parent : null,
    owner: newData.owner ? newData.owner : null,
    phone: newData.phone ? newData.phone : '',
    websiteUrl: newData.websiteUrl ? newData.websiteUrl : null,
    rankingIndex: newData.rankingIndex,
    vatNumber: newData.vatNumber ? newData.vatNumber : null,
    vatExempt: newData.countryCode === 'DE' && newData.vatExempt === true,
    email: newData.email,
    emailForAlert: newData.emailForAlert ? newData.emailForAlert : null,
    emailForInvoice: newData.emailForInvoice ? newData.emailForInvoice : null,
    dataProtectionOfficerEmail: newData.dataProtectionOfficerEmail ? newData.dataProtectionOfficerEmail : null,
    legalRepresentativeEmail: newData.legalRepresentativeEmail ? newData.legalRepresentativeEmail : null,
    procurementEmail: newData.procurementEmail ? newData.procurementEmail : null,
    paymentType: newData.paymentType,
    countryName: newData.countryName,
    countryCode: newData.countryCode,
    addressCity: newData.addressCity,
    addressZip: newData.addressZip ? newData.addressZip : null,
    addressStreet: newData.addressStreet,
    addressState: newData.addressState ? newData.addressState : null,
    password: newData.password ? newData.password : null,
    emailVerified: newData.emailVerified,
    sendVerificationEmail: false,
    testTtl: newData.testTtl,
    enabled: newData.enabled,
    testRetriesEnabled: newData.testRetriesEnabled,
    source: newData.source !== '' ? newData.source : null,
    convertedBy: newData.convertedBy ? newData.convertedBy : null,
    salesRepresentative: newData.salesRepresentative ? newData.salesRepresentative : null,
    ipWhitelistAddressList: newData.ipWhitelistAddressList ? newData.ipWhitelistAddressList : null,
    ipWhitelistEnabled: newData.ipWhitelistEnabled,
    minBalance: minBalanceToMilliEuros(newData.minBalance ?? 0),
    comment: newData.comment ? newData.comment : null,
  };
}

export { UserSearchEvent } from './user-search-event.model';

export interface UserTestCount {
  date: string;
  mainaccountId: number;
  testBilled: number;
  testSubmitted: number;
}

export class TrustedDeviceRequestParams {
  size = 20;
  page = 1;

  sort: string[] = [];

  setSort(propertyName: string, direction: string) {
    this.sort.push(propertyName + (direction === 'desc' ? ',desc' : ''));
  }

  removeSort(propertyName: string) {
    this.sort = this.sort.filter(_ => _.indexOf(propertyName) === -1);
  }

  resetSort() {
    this.sort = [];
  }
}
